'use client';

import { useMemo } from 'react';
import type { ToolExecutionHar } from '@terra/shared-contracts-tracker';
import { useOptionalCredentialsContext } from '../credentials';
import {
  isMongoId,
  useEntityResolver,
  type ResolvedCredential,
} from '../trace-view/context/EntityResolverContext';

function isResolvedCredential(entity: unknown): entity is ResolvedCredential {
  if (typeof entity !== 'object' || entity === null) {
    return false;
  }
  const record = entity as Record<string, unknown>;
  return typeof record.id === 'string' && typeof record.name === 'string';
}

function toTrimmed(value: string | undefined | null): string | undefined {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : undefined;
}

export type MatchedCredentialFields = {
  role?: string;
  username?: string;
};

function usernameFromResolvedCredential(
  entity: ResolvedCredential,
): string | undefined {
  const fromUsername = toTrimmed(entity.username);
  if (fromUsername) {
    return fromUsername;
  }
  const fromName = toTrimmed(entity.name);
  if (!fromName || fromName.startsWith('Credential ')) {
    return undefined;
  }
  const role = toTrimmed(entity.role);
  if (role && fromName === role) {
    return undefined;
  }
  const roleInParensMatch = fromName.match(/^\((.+)\)$/);
  if (role && roleInParensMatch?.[1]?.trim() === role) {
    return undefined;
  }
  return fromName;
}

export function resolveMatchedCredentialFields({
  matchedCredentialId,
  resolve,
  credentialsPreview,
}: {
  matchedCredentialId: string | undefined;
  resolve: ReturnType<typeof useEntityResolver>['resolve'];
  credentialsPreview: { id: string; role: string; username: string }[];
}): MatchedCredentialFields | null {
  const id = matchedCredentialId?.trim();
  if (!id || !isMongoId(id)) {
    return null;
  }

  const entity = resolve('credential', id);
  if (isResolvedCredential(entity)) {
    const role = toTrimmed(entity.role) ?? toTrimmed(entity.type) ?? undefined;
    const username = usernameFromResolvedCredential(entity);
    return { role, username };
  }

  const preview = credentialsPreview.find((c) => c.id === id);
  if (preview) {
    return {
      role: toTrimmed(preview.role),
      username: toTrimmed(preview.username),
    };
  }

  return {};
}

export function useMatchedCredentialFields(
  har: ToolExecutionHar | null | undefined,
): MatchedCredentialFields | null {
  const { resolve } = useEntityResolver();
  const optionalCredentials = useOptionalCredentialsContext();

  return useMemo(
    () =>
      resolveMatchedCredentialFields({
        matchedCredentialId: har?.matchedCredentialId,
        resolve,
        credentialsPreview: optionalCredentials?.credentialsPreview ?? [],
      }),
    [har?.matchedCredentialId, resolve, optionalCredentials],
  );
}
